import { useCallback, useState } from 'react'
import { keepPreviousData, QueryKey, useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'

type UsePollingQueryProps<T> = {
  queryKey: QueryKey
  queryFn: () => Promise<T>
  intervalMs: number
  startOnLoad?: boolean
  /** Still fetch on load and when the query key changes while polling is stopped. */
  fetchWhilePaused?: boolean
  /** Keep returning the previous data while a new query key loads. */
  keepDataOnKeyChange?: boolean
}

const usePollingQuery = <T>({
  queryKey,
  queryFn,
  intervalMs,
  startOnLoad = false,
  fetchWhilePaused = false,
  keepDataOnKeyChange = false,
}: UsePollingQueryProps<T>) => {
  const [isPolling, setIsPolling] = useState(startOnLoad)

  const query = useQuery({
    queryKey,
    queryFn,
    enabled: isPolling || fetchWhilePaused,
    retry: 0,
    refetchInterval: isPolling ? intervalMs : false,
    refetchOnWindowFocus: isPolling,
    refetchOnReconnect: isPolling,
    placeholderData: keepDataOnKeyChange ? keepPreviousData : undefined,
  })

  const startRefresh = useCallback(async () => {
    setIsPolling(true)
    await query.refetch()
  }, [query])

  const stopRefresh = useCallback(() => {
    setIsPolling(false)
  }, [])

  const loadedTime = query.dataUpdatedAt
    ? dayjs(query.dataUpdatedAt).format('HH:mm:ss')
    : ''

  return {
    data: query.data,
    isLoading: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
    error: query.error,
    refetch: query.refetch,
    startRefresh,
    stopRefresh,
    loadedTime,
  }
}

export default usePollingQuery
